import React, { useState, useEffect } from 'react';
import { User, TrendingUp, Award, Activity, BarChart3, Loader2, Trophy } from 'lucide-react';
import { toast } from 'sonner';

const Profile = () => {
  const [progress, setProgress] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [handsHistory, setHandsHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    loadProfileData();
  }, []);

  const loadProfileData = async () => {
    setLoading(true);
    try {
      const [progressRes, statsRes, handsRes] = await Promise.all([
        fetch(`${API_URL}/api/platform/progress`, { credentials: 'include' }),
        fetch(`${API_URL}/api/platform/statistics`, { credentials: 'include' }),
        fetch(`${API_URL}/api/platform/hands/history?limit=10`, { credentials: 'include' })
      ]);

      if (progressRes.ok) {
        const data = await progressRes.json();
        setProgress(data);
      }

      if (statsRes.ok) {
        const data = await statsRes.json();
        setStatistics(data);
      }

      if (handsRes.ok) {
        const data = await handsRes.json();
        setHandsHistory(data.hands);
      }
    } catch (error) {
      toast.error('Failed to load profile data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0F14] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#C9A227]" />
      </div>
    );
  }

  const playingStyle = statistics?.playing_style || {};

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0B0F14] to-[#0A241C] py-8">
      <div className="max-w-6xl mx-auto px-4 space-y-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-heading font-bold mb-2">Your Profile</h1>
          <p className="text-foreground/60">Track your progress and analyze your game</p>
        </div>

        {/* XP & Level Card */}
        <div className="bg-gradient-to-br from-[#121923] to-[#0A241C] border-2 border-[#C9A227] rounded-xl p-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <p className="text-sm text-foreground/60">Current Level</p>
              <p className="text-5xl font-heading font-bold text-[#C9A227]">{progress?.current_level || 1}</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-foreground/60">Total XP</p>
              <p className="text-3xl font-bold">{progress?.xp?.toLocaleString() || 0}</p>
            </div>
          </div>

          {/* Progress Bar */}
          {!progress?.is_max_level && (
            <div>
              <div className="flex justify-between text-sm text-foreground/60 mb-2">
                <span>Level {progress?.current_level}</span>
                <span>{progress?.xp_needed} XP to Level {progress?.next_level}</span>
              </div>
              <div className="w-full h-3 bg-[#0B0F14] rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-[#C9A227] to-[#E1C25B] rounded-full transition-all duration-500"
                  style={{ width: `${progress?.xp_progress || 0}%` }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-[#121923] border border-white/10 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-2">
              <Activity className="w-5 h-5 text-[#C9A227]" />
              <span className="text-sm text-foreground/60">Hands Played</span>
            </div>
            <p className="text-3xl font-bold">{statistics?.hands_played || 0}</p>
          </div>

          <div className="bg-[#121923] border border-white/10 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-2">
              <Trophy className="w-5 h-5 text-green-500" />
              <span className="text-sm text-foreground/60">Hands Won</span>
            </div>
            <p className="text-3xl font-bold">{statistics?.hands_won || 0}</p>
          </div>

          <div className="bg-[#121923] border border-white/10 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-2">
              <TrendingUp className="w-5 h-5 text-blue-500" />
              <span className="text-sm text-foreground/60">Win Rate</span>
            </div>
            <p className="text-3xl font-bold">
              {statistics?.hands_played > 0
                ? Math.round((statistics.hands_won / statistics.hands_played) * 100)
                : 0}%
            </p>
          </div>
        </div>

        {/* Playing Style Analysis */}
        <div className="bg-gradient-to-br from-[#0F3227] to-[#0A241C] border-2 border-[#C9A227]/30 rounded-xl p-8">
          <h2 className="text-2xl font-heading font-bold mb-4 flex items-center gap-3">
            <BarChart3 className="w-6 h-6 text-[#C9A227]" />
            Your Playing Style
          </h2>

          <div className="mb-6">
            <div className="inline-block px-6 py-3 bg-[#C9A227] text-[#0B0F14] rounded-lg font-heading font-bold text-xl mb-3">
              {playingStyle.style || 'Analyzing...'}
            </div>
            <p className="text-foreground/80 leading-relaxed">{playingStyle.description}</p>
          </div>

          {/* Style Metrics */}
          {statistics?.hands_played >= 20 && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-[#121923]/50 rounded-lg p-4 border border-white/5">
                <p className="text-xs text-foreground/60 mb-1">VPIP (Voluntarily Put $ In Pot)</p>
                <p className="text-2xl font-bold text-[#C9A227]">{playingStyle.vpip}%</p>
              </div>
              <div className="bg-[#121923]/50 rounded-lg p-4 border border-white/5">
                <p className="text-xs text-foreground/60 mb-1">PFR (Pre-Flop Raise)</p>
                <p className="text-2xl font-bold text-[#C9A227]">{playingStyle.pfr}%</p>
              </div>
              <div className="bg-[#121923]/50 rounded-lg p-4 border border-white/5">
                <p className="text-xs text-foreground/60 mb-1">Aggression Factor</p>
                <p className="text-2xl font-bold text-[#C9A227]">{playingStyle.aggression_factor}</p>
              </div>
            </div>
          )}

          {statistics?.hands_played < 20 && (
            <p className="text-sm text-foreground/60 italic">Play 20+ hands to see your detailed statistics</p>
          )}
        </div>

        {/* Badges */}
        {progress?.badge_details && progress.badge_details.length > 0 && (
          <div className="bg-[#121923] border border-white/10 rounded-xl p-6">
            <h2 className="text-xl font-heading font-bold mb-4 flex items-center gap-3">
              <Award className="w-5 h-5 text-[#C9A227]" />
              Badges Earned
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {progress.badge_details.map((badge) => (
                <div
                  key={badge.key}
                  className="bg-[#0F3227] border-2 border-[#C9A227]/30 rounded-lg p-4 text-center"
                  data-testid={`badge-${badge.key}`}
                >
                  <div className="text-3xl mb-2">🏆</div>
                  <p className="font-semibold text-sm mb-1">{badge.name}</p>
                  <p className="text-xs text-foreground/60">{badge.description}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recent Hands */}
        <div className="bg-[#121923] border border-white/10 rounded-xl p-6">
          <h2 className="text-xl font-heading font-bold mb-4">Recent Hands</h2>
          {handsHistory.length === 0 ? (
            <p className="text-center py-8 text-foreground/40">No hands played yet</p>
          ) : (
            <div className="space-y-3">
              {handsHistory.map((hand) => (
                <div
                  key={hand.hand_id}
                  className="bg-[#0A241C] border border-white/5 rounded-lg p-4"
                  data-testid={`hand-${hand.hand_id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`px-3 py-1 rounded-full text-xs font-bold ${
                        hand.result === 'won'
                          ? 'bg-green-600/20 text-green-400'
                          : 'bg-red-600/20 text-red-400'
                      }`}>
                        {hand.result.toUpperCase()}
                      </div>
                      <div>
                        <p className="text-sm">
                          <span className="font-semibold">Your hand:</span> {hand.player_hand?.join(' ') || 'N/A'}
                        </p>
                        {hand.community_cards && hand.community_cards.length > 0 && (
                          <p className="text-xs text-foreground/60">Board: {hand.community_cards.join(' ')}</p>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-[#C9A227]">${hand.pot_size}</p>
                      <p className="text-xs text-foreground/60">{new Date(hand.created_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Profile;